import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowLeft, ArrowRight } from "lucide-react";
import { PORTFOLIO, PORTFOLIO_CATEGORIES } from "@/data/site";

const heightMap = { tall: "h-[440px]", mid: "h-[340px]", short: "h-[260px]" };

export default function Portfolio() {
  const [cat, setCat] = useState("All");
  const [lightboxIdx, setLightboxIdx] = useState(null);

  const items = useMemo(
    () => (cat === "All" ? PORTFOLIO : PORTFOLIO.filter((p) => p.category === cat)),
    [cat]
  );

  const close = () => setLightboxIdx(null);
  const next = () => setLightboxIdx((i) => (i + 1) % items.length);
  const prev = () => setLightboxIdx((i) => (i - 1 + items.length) % items.length);

  return (
    <section id="portfolio" data-testid="portfolio-section" className="bg-white py-24 md:py-32">
      <div className="pz-container">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div>
            <span className="pz-overline">Selected work</span>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0F172A]">
              A portfolio that<br />speaks for itself.
            </h2>
            <div className="pz-gold-line mt-6" />
          </div>

          <div className="flex flex-wrap gap-2">
            {PORTFOLIO_CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                data-testid={`portfolio-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all border ${
                  cat === c
                    ? "bg-[#0F172A] text-white border-[#0F172A]"
                    : "bg-white text-slate-600 border-slate-200 hover:border-[#0F172A] hover:text-[#0F172A]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 space-y-6">
          <AnimatePresence>
            {items.map((p, i) => (
              <motion.button
                type="button"
                key={p.id}
                onClick={() => setLightboxIdx(i)}
                data-testid={`portfolio-item-${p.id}`}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.45 }}
                className={`group break-inside-avoid relative w-full overflow-hidden rounded-2xl block ${heightMap[p.h]}`}
              >
                <img
                  src={p.img}
                  alt={p.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/85 via-[#0F172A]/0 to-transparent opacity-90 group-hover:opacity-100 transition-opacity" />
                <div className="absolute inset-x-5 bottom-5 text-left translate-y-2 group-hover:translate-y-0 transition-transform">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-[#F59E0B] font-semibold">{p.category}</div>
                  <div className="mt-1 text-white font-display text-xl font-semibold">{p.title}</div>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxIdx !== null && items[lightboxIdx] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-[#0F172A]/95 flex items-center justify-center p-4"
            data-testid="portfolio-lightbox"
            onClick={close}
          >
            <button
              onClick={(e) => { e.stopPropagation(); close(); }}
              className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
              aria-label="Close lightbox"
              data-testid="lightbox-close"
            >
              <X className="w-5 h-5" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); prev(); }}
              className="absolute left-5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
              aria-label="Previous"
              data-testid="lightbox-prev"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); next(); }}
              className="absolute right-5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
              aria-label="Next"
              data-testid="lightbox-next"
            >
              <ArrowRight className="w-5 h-5" />
            </button>
            <motion.div
              key={items[lightboxIdx].id}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="max-w-5xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={items[lightboxIdx].img}
                alt={items[lightboxIdx].title}
                className="w-full max-h-[80vh] object-contain rounded-2xl"
              />
              <div className="mt-4 text-center">
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#F59E0B] font-semibold">
                  {items[lightboxIdx].category}
                </div>
                <div className="mt-1 text-white font-display text-2xl">{items[lightboxIdx].title}</div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
